import React from "react";
import { FilterFilled } from "@ant-design/icons";
import { ensureColumnHeaderWidths } from "./tableColumnWidth";

export const OPERATOR_SHORT = {
    gt: ">",
    lt: "<",
    gte: "≥",
    lte: "≤",
    eq: "=",
    contains: "contains",
};

/**
 * @param {{ column?: string, columnLabel?: string, operator?: string, value?: unknown }} filter
 */
export function formatFilterDisplayValue(filter) {
    if (!filter) return "";
    if (filter.column === "show_under_advertising_cost") {
        return filter.value === "1" || filter.value === 1 ? "Yes" : "No";
    }
    return String(filter.value ?? "");
}

/**
 * @param {Array<{ column?: string }>} activeFilters
 * @returns {Set<string>}
 */
export function getActiveFilterColumnKeys(activeFilters) {
    return new Set(
        (activeFilters || [])
            .map((f) => f.column)
            .filter(Boolean)
    );
}

/**
 * Move columns with active filters to the left (after pinned columns), preserving relative order.
 *
 * @param {Array<{ key: string, fixed?: string }>} columns
 * @param {Array<{ column?: string }>} activeFilters
 * @param {{ pinnedLeftKeys?: string[], pinnedRightKeys?: string[] }} [options]
 */
export function reorderColumnsForActiveFilters(columns, activeFilters, options = {}) {
    const { pinnedLeftKeys = [], pinnedRightKeys = [] } = options;
    const filteredKeys = getActiveFilterColumnKeys(activeFilters);

    const leftPinned = [];
    const rightPinned = [];
    const middle = [];

    for (const col of columns) {
        const key = col.key;
        if (pinnedLeftKeys.includes(key) || col.fixed === "left") {
            leftPinned.push(col);
        } else if (pinnedRightKeys.includes(key) || col.fixed === "right") {
            rightPinned.push(col);
        } else {
            middle.push(col);
        }
    }

    const filteredMiddle = middle.filter((col) => filteredKeys.has(col.key));
    const unfilteredMiddle = middle.filter((col) => !filteredKeys.has(col.key));

    return [...leftPinned, ...filteredMiddle, ...unfilteredMiddle, ...rightPinned];
}

function FilterIndicator() {
    return (
        <FilterFilled
            style={{ fontSize: 11, color: "#f45b68", flexShrink: 0 }}
            aria-label="Filter active"
        />
    );
}

/**
 * @param {React.ReactNode} title
 */
export function withFilterIndicator(title) {
    if (title == null || title === false) return title;
    return (
        <span
            className="table-filter-header-indicator d-inline-flex align-items-center gap-1"
            style={{ whiteSpace: "nowrap", flexWrap: "nowrap" }}
        >
            {title}
            <FilterIndicator />
        </span>
    );
}

/**
 * @param {Array<{ key: string, title?: React.ReactNode }>} columns
 * @param {Array<{ column?: string }>} activeFilters
 */
export function enhanceColumnsWithFilterIndicators(columns, activeFilters) {
    const filteredKeys = getActiveFilterColumnKeys(activeFilters);
    if (!filteredKeys.size) return columns;

    return columns.map((col) => {
        if (!filteredKeys.has(col.key)) return col;
        return {
            ...col,
            title: withFilterIndicator(col.title),
        };
    });
}

/**
 * Filter visible columns, reorder filtered columns left, and add header indicators.
 *
 * @param {Array<{ key: string }>} allColumns
 * @param {string[]} visibleColumnKeys
 * @param {Array<{ column?: string }>} activeFilters
 * @param {{ pinnedLeftKeys?: string[], pinnedRightKeys?: string[] }} [options]
 */
export function buildTableColumnsWithFilters(allColumns, visibleColumnKeys, activeFilters, options = {}) {
    const visible = allColumns.filter((col) => visibleColumnKeys.includes(col.key));
    const reordered = reorderColumnsForActiveFilters(visible, activeFilters, options);
    const withIndicators = enhanceColumnsWithFilterIndicators(reordered, activeFilters);
    const filteredKeys = getActiveFilterColumnKeys(activeFilters);
    return ensureColumnHeaderWidths(withIndicators, filteredKeys);
}
